import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ExportsControllerExportTicketsJson$Params } from '@api/fn/exports/exports-controller-export-tickets-json';
import { TicketExportItemDto } from '@api/models';
import { ExportsService } from '@api/services';
import { TicketStatus } from '@page/tickets/enums/ticket-status';
import { Column, DataSource, DatatableComponent, FiltersData } from '@shared/components/datatable/datatable.component';
import { DatePickerFieldComponent } from '@shared/components/date-picker-field/date-picker-field.component';
import {
   FieldEntityType,
   SelectEntityFieldComponent,
} from '@shared/components/general-selects/select-entity-field.component';
import { SelectEventFieldComponent } from '@shared/components/general-selects/select-event-field.component';
import { InputFieldComponent } from '@shared/components/input-field/input-field.component';
import { SelectField, SelectFieldComponent } from '@shared/components/select-field/select-field.component';
import { SubscriptionDirective } from '@shared/directives/subscriptions.directive';
import { saveBlob } from '@shared/utils/file';
import moment from 'moment';
import { sampleTime } from 'rxjs';

type ExportTicketItem = ExportsControllerExportTicketsJson$Params & FiltersData;

@Component({
   selector: 'app-tickets-export',
   imports: [
      CommonModule,
      FormsModule,
      ReactiveFormsModule,
      DatatableComponent,
      InputFieldComponent,
      SelectFieldComponent,
      DatePickerFieldComponent,
      MatButtonModule,
      SelectEventFieldComponent,
      MatCardModule,
      MatIconModule,
      MatMenuModule,
      MatProgressSpinner,
      SelectEntityFieldComponent,
   ],
   templateUrl: './tickets-export.component.html',
   styleUrl: './tickets-export.component.scss',
})
export class TicketsExportComponent extends SubscriptionDirective {
   dataSource: DataSource<TicketExportItemDto, ExportTicketItem> | undefined;
   columns: Column<TicketExportItemDto>[] = [
      {
         name: 'Created',
         type: 'dateTime',
         field: 'createdAt',
      },
      {
         name: 'Event',
         field: 'event',
      },
      {
         name: 'Customer',
         field: 'customer',
      },
      {
         name: 'Email',
         field: 'email',
      },
      {
         name: 'Phone',
         field: 'phone',
      },
   ];

   readonly ticketStatus: SelectField[] = [
      { name: 'Not Picked', id: TicketStatus.CREATED },
      { name: 'Open', id: TicketStatus.PICKED },
      { name: 'Checkin', id: TicketStatus.CHECKED_IN },
      { name: 'Ghost', id: 4 },
   ];

   readonly paymentDepositOptions: SelectField[] = [
      { name: 'Deposit Cash', id: 1 },
      { name: 'Deposit POS', id: 2 },
      { name: 'Deposit Web', id: 3 },
   ];

   readonly paymentOptions: SelectField[] = [
      { name: ' Cash', id: 1 },
      { name: ' POS', id: 2 },
      { name: ' Web', id: 3 },
   ];

   readonly startOfDay = moment().startOf('day');
   readonly endOfDay = moment().endOf('day');

   loading = false;

   searchForm = this.formBuilder.nonNullable.group({
      search: [''],
      ticketTypeId: [undefined as any as number],
      eventsIds: [[] as number[]],
      fromDate: [this.startOfDay],
      toDate: [this.endOfDay],
      eventCategoryId: [undefined as any as number],
      ticketStatus: [],
      paymentOption: [],
      paymentDepositOption: [],
   });

   get emptyFilters() {
      const { search, eventsIds, fromDate, toDate, eventCategoryId, paymentOption, ticketTypeId } = this.searchForm.value;
      return (
         !search &&
         !ticketTypeId &&
         !eventCategoryId &&
         eventsIds?.length === 0 &&
         fromDate?.toISOString() === this.startOfDay.toISOString() &&
         toDate?.toISOString() === this.endOfDay.toISOString() &&
         paymentOption === 0
      );
   }

   get filters() {
      const { value } = this.searchForm;
      return {
         ...value,
         fromDate: (value.fromDate as any)?.toDate()?.toISOString(),
         toDate: (value.toDate as any)?.toDate()?.toISOString(),
      };
   }

   constructor(
      private formBuilder: FormBuilder,
      private exportsService: ExportsService
   ) {
      super();
      this.setupDataSource();
      this.initSearch();
   }

   setupDataSource() {
      this.dataSource = new DataSource<TicketExportItemDto, ExportTicketItem>()
         .setInitialFilters(this.filters)
         .onSearchPageable((params) => {
            return this.exportsService.exportsControllerExportTicketsJson(params);
         });
   }

   initSearch() {
      this.subs = this.searchForm.valueChanges.pipe(sampleTime(600)).subscribe(() => this.onSearch());
   }

   onSearch() {
      this.dataSource?.applyFilters(this.filters);
   }

   clearFilter() {
      this.searchForm.reset();
   }

   downloadAll() {
      this.loading = true;
      const { value } = this.searchForm;

      this.subs = this.exportsService.exportsControllerExportTicketsCsv(this.filters).subscribe((data) => {
         saveBlob(
            data,
            `export-tickets-${value.fromDate!.toDate().toLocaleDateString()}-${value.toDate!.toDate().toLocaleDateString()}.csv`
         );

         this.loading = false;
      });
   }

   protected readonly FieldEntityType = FieldEntityType;
}
